/* Notes d'examen publiées, vue élève/responsable (bloc 08-B). */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Result {
  exam_copy_id: string;
  exam_title: string;
  total_score: number;
  total_max: number;
  overall_feedback: string;
  items: Array<{ section_title: string; score: number; max: number; feedback: string; letter?: string; letter_label?: string }>;
}

interface Absence {
  exam_copy_id: string;
  exam_title: string;
}

export default function StudentExamResults({ studentId }: { studentId: string }) {
  const [results, setResults] = useState<Result[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api.parentOrTutor.student.examResults
      .query({ studentId })
      .then((r) => setResults(r as Result[]))
      .catch(() => {
        /* section optionnelle */
      });
    api.parentOrTutor.student.examAbsences
      .query({ studentId })
      .then((r) => setAbsences(r as Absence[]))
      .catch(() => {
        /* section optionnelle */
      });
  }, [studentId]);

  if (results.length === 0 && absences.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 8px' }}>Examens</h2>
      {absences.map((a) => (
        <div key={a.exam_copy_id} className="w-full mt-[6px] flex items-center justify-between gap-2 px-1 min-h-[40px]">
          <span className="text-[13px] font-bold text-foreground [overflow-wrap:anywhere]">{a.exam_title}</span>
          <span className="flex-none text-[12px] font-bold px-2 py-[2px] rounded-full bg-[var(--naia-bg,rgba(11,42,74,0.08))] text-[var(--naia-text-secondary)]">
            Absent(e)
          </span>
        </div>
      ))}
      {results.map((r) => (
        <div key={r.exam_copy_id} className="w-full mt-[6px]">
          <button
            type="button"
            className="inline-flex items-center gap-[6px] min-h-[40px] px-1 border-none bg-transparent text-primary text-[13px] font-bold cursor-pointer"
            onClick={() => setOpen((v) => (v === r.exam_copy_id ? null : r.exam_copy_id))}
            aria-expanded={open === r.exam_copy_id}
          >
            {open === r.exam_copy_id ? '▾' : '▸'} {r.exam_title} — {r.total_score}/{r.total_max}
          </button>
          {open === r.exam_copy_id && (
            <div className="mt-2 p-[14px] border border-border rounded-card bg-card flex flex-col gap-3">
              {r.total_max > 0 && (
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-extrabold leading-none text-foreground [font-variant-numeric:tabular-nums]">{r.total_score}</span>
                  <span className="text-[15px] font-semibold text-muted-foreground">/ {r.total_max}</span>
                </div>
              )}
              {r.overall_feedback && (
                <p className="m-0 p-[10px_12px] rounded-[10px] bg-[var(--naia-bg,rgba(11,42,74,0.04))] text-[13.5px] leading-relaxed text-foreground">{r.overall_feedback}</p>
              )}
              {r.items.length > 0 && (
                <div className="flex flex-col gap-[10px]">
                  {r.items.map((it, i) => (
                    <div key={i} className="pb-[10px] border-b border-border last:border-b-0 last:pb-0">
                      <div className="flex items-baseline justify-between gap-[10px]">
                        <span className="text-[13.5px] font-bold text-foreground [overflow-wrap:anywhere]">{it.section_title || `Partie ${i + 1}`}</span>
                        <span className="flex-none text-[14px] font-bold text-foreground [font-variant-numeric:tabular-nums]">{it.letter ? `${it.letter}${it.letter_label ? ` — ${it.letter_label}` : ''}` : `${it.score}/${it.max}`}</span>
                      </div>
                      {it.feedback && <p className="mt-1 m-0 text-[13px] leading-[1.45] text-muted-foreground">{it.feedback}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
